import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { supabase } from "@/lib/supabase";
import { fetchOwnerSpace } from "@/hooks/queries/useOwnerSpace";
import { toast } from "@/hooks/use-toast";
import { fireEvent } from "@/lib/events";
import { resizeImageToSquare } from "@/lib/imageResize";
import AvatarCropper from "@/components/AvatarCropper";
import ChildAvatar from "@/components/ChildAvatar";
import ChildColorPicker from "@/components/ChildColorPicker";
import { autoAssignColor } from "@/config/childColors";
import { useLanguages } from "@/hooks/useLanguages";
import { useContentLanguages } from "@/hooks/useContentLanguages";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Props {
  userId: string;
  onComplete: () => void;
}

function getAgePreview(name: string, birthdate: string): { months: number; years: number; totalMonths: number } | null {
  if (!name || !birthdate) return null;
  const birth = new Date(birthdate);
  if (isNaN(birth.getTime()) || birth > new Date()) return null;
  const now = new Date();
  const totalMonths =
    (now.getFullYear() - birth.getFullYear()) * 12 +
    (now.getMonth() - birth.getMonth());
  if (totalMonths < 0) return null;
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  return { months, years, totalMonths };
}

type ChildGender = "male" | "female" | "neutral";

const OnboardingFlow = ({ userId, onComplete }: Props) => {
  const { t } = useTranslation();
  const { languages, isLoading: langsLoading } = useLanguages('active');
  const { languages: contentLanguages } = useContentLanguages({ activeOnly: true });
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [nickname, setNickname] = useState("");
  const [childName, setChildName] = useState("");
  const [birthdate, setBirthdate] = useState("");
  const [gender, setGender] = useState<ChildGender>("neutral");
  const [avatarColor, setAvatarColor] = useState<string>(autoAssignColor("neutral", ""));
  const [colorManuallySet, setColorManuallySet] = useState(false);
  const [language, setLanguage] = useState<string>("en");
  const [contentLanguage, setContentLanguage] = useState<string>("en");
  const [contentLanguageTouched, setContentLanguageTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [childSkipped, setChildSkipped] = useState(false);

  // Preset content_language from the browser locale when the catalog
  // loads — only until the user explicitly picks a value, after which
  // we leave their choice alone.
  useEffect(() => {
    if (contentLanguageTouched || contentLanguages.length === 0) return;
    const browserLang = navigator.language?.slice(0, 2).toLowerCase();
    const match = contentLanguages.find((l) => l.code === browserLang);
    if (match) setContentLanguage(match.code);
  }, [contentLanguages, contentLanguageTouched]);

  // Optional child avatar captured during step 2 — uploaded after insert
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [avatarImageSrc, setAvatarImageSrc] = useState<string | null>(null);
  const [avatarBlob, setAvatarBlob] = useState<Blob | null>(null);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState<string | null>(null);

  const handleAvatarFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setAvatarImageSrc(reader.result as string);
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleAvatarCropDone = (blob: Blob) => {
    setAvatarBlob(blob);
    if (avatarPreviewUrl) URL.revokeObjectURL(avatarPreviewUrl);
    setAvatarPreviewUrl(URL.createObjectURL(blob));
    setAvatarImageSrc(null);
  };

  const handleStep1 = () => {
    if (!nickname.trim()) return;
    setStep(2);
  };

  const handleStep2 = () => {
    if (!childName.trim() || !birthdate) return;
    setChildSkipped(false);
    setStep(3);
  };

  const handleSkipChild = () => {
    setChildSkipped(true);
    setStep(3);
  };

  const handleComplete = async () => {
    setSaving(true);

    const { error: profileError } = await supabase
      .from("profiles")
      .update({
        nickname: nickname.trim(),
        language,
        content_language: contentLanguage,
      })
      .eq("id", userId);

    if (profileError) {
      console.error(profileError);
      toast({ title: t("onboarding.error.nicknameSave"), variant: "destructive" });
      setSaving(false);
      return;
    }

    // Insert child only when the user filled in the child step (not skipped).
    if (!childSkipped && childName.trim() && birthdate) {
      // Fetch the user's space — children are scoped to a space, not a user.
      const spaceId = await fetchOwnerSpace(userId);

      if (!spaceId) {
        toast({ title: t("onboarding.error.childSave"), description: "No space found — please reload", variant: "destructive" });
        setSaving(false);
        return;
      }

      const { data: insertedChild, error: childError } = await supabase
        .from("children")
        .insert({
          space_id: spaceId,
          name: childName.trim(),
          birthdate,
          gender,
          avatar_color: avatarColor,
        })
        .select("id")
        .single();

      if (childError || !insertedChild) {
        console.error(childError);
        toast({ title: t("onboarding.error.childSave"), variant: "destructive" });
        setSaving(false);
        return;
      }

      // Upload optional avatar if user added one in step 2
      if (avatarBlob) {
        try {
          const childId = (insertedChild as { id: string }).id;
          const path = `${userId}/${childId}.jpg`;
          const resized = await resizeImageToSquare(avatarBlob, 200);
          await supabase.storage
            .from("avatars")
            .upload(path, resized, {
              contentType: "image/jpeg",
              upsert: true,
              cacheControl: "3600",
            });
          // Store the storage path — ChildAvatar generates signed URLs at display time.
          await supabase
            .from("children")
            .update({ avatar_url: `${path}?v=${Date.now()}` })
            .eq("id", childId);
        } catch (err) {
          // Non-fatal — proceed even if avatar upload fails
          console.error("[onboarding] avatar upload failed:", err);
        }
      }
    }

    fireEvent("onboarding.completed", { userId, durationSeconds: 0 });
    onComplete();
  };

  const ageData = getAgePreview(childName, birthdate);
  let agePreview = "";
  if (ageData) {
    if (ageData.totalMonths === 0) {
      agePreview = t("onboarding.step2.ageFireToday", { name: childName });
    } else {
      let age: string;
      if (ageData.years === 0) {
        age = `${ageData.months} month${ageData.months !== 1 ? "s" : ""}`;
      } else if (ageData.months === 0) {
        age = `${ageData.years} year${ageData.years !== 1 ? "s" : ""}`;
      } else {
        age = `${ageData.years} year${ageData.years !== 1 ? "s" : ""} and ${ageData.months} month${ageData.months !== 1 ? "s" : ""}`;
      }
      agePreview = `${childName} is ${age} old`;
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-6 animate-page-enter">
      {/* Decorative organic shapes */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-primary/[0.06] animate-float" />
        <div className="absolute top-1/3 -left-32 h-96 w-96 rounded-full bg-accent/[0.07] animate-float-slow" />
        <div className="absolute -bottom-16 right-10 h-56 w-56 rounded-full bg-primary/[0.04] animate-float-slower" />
      </div>

      <div className="relative z-10 w-full max-w-sm">
        {/* Step indicator */}
        <div className="mb-8 flex justify-center gap-1.5">
          {([1, 2, 3, 4] as const).map((s) => (
            <div
              key={s}
              className={`h-1.5 w-8 rounded-full transition-colors ${step === s ? "bg-primary" : step > s ? "bg-primary/50" : "bg-primary/20"}`}
            />
          ))}
        </div>

        {/* ── Step 1: Nickname ─────────────────────────────────────────────── */}
        {step === 1 && (
          <div className="space-y-6 animate-page-enter">
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-3">{t("onboarding.step1.welcome")}</p>
              <h1 className="font-display text-3xl tracking-tight text-foreground">
                {t("onboarding.step1.heading")}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {t("onboarding.step1.subtext")}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="nickname">{t("onboarding.step1.nameLabel")}</Label>
              <Input
                id="nickname"
                placeholder={t("onboarding.step1.namePlaceholder")}
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleStep1()}
                className="h-12 text-base bg-card"
                autoFocus
              />
            </div>

            <Button
              className="w-full h-12 text-base font-medium"
              onClick={handleStep1}
              disabled={!nickname.trim()}
            >
              {t("common.continue")}
            </Button>
          </div>
        )}

        {/* ── Step 2: First child ──────────────────────────────────────────── */}
        {step === 2 && (
          <div className="space-y-6 animate-page-enter">
            <div className="text-center">
              <h1 className="font-display text-3xl tracking-tight text-foreground">
                {t("onboarding.step2.heading")}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {t("onboarding.step2.subtext")}
              </p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="childName">{t("onboarding.step2.nameLabel")}</Label>
                <Input
                  id="childName"
                  placeholder={t("onboarding.step2.namePlaceholder")}
                  value={childName}
                  onChange={(e) => setChildName(e.target.value)}
                  className="h-12 text-base bg-card"
                  autoFocus
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="birthdate">{t("onboarding.step2.dobLabel")}</Label>
                <Input
                  id="birthdate"
                  type="date"
                  value={birthdate}
                  onChange={(e) => setBirthdate(e.target.value)}
                  max={new Date().toISOString().split("T")[0]}
                  className="h-12 text-base bg-card"
                />
              </div>

              <div className="space-y-2">
                <Label>{t("onboarding.step2.genderLabel")}</Label>
                <ToggleGroup
                  type="single"
                  value={gender}
                  onValueChange={(v) => {
                    if (!v) return;
                    const g = v as ChildGender;
                    setGender(g);
                    if (!colorManuallySet) {
                      setAvatarColor(autoAssignColor(g, childName));
                    }
                  }}
                  className="grid grid-cols-3 gap-2"
                >
                  <ToggleGroupItem
                    value="female"
                    className="h-12 rounded-md border bg-card text-base data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                  >
                    {t("onboarding.step2.genderFemale")}
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    value="male"
                    className="h-12 rounded-md border bg-card text-base data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                  >
                    {t("onboarding.step2.genderMale")}
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    value="neutral"
                    className="h-12 rounded-md border bg-card text-sm data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                  >
                    {t("onboarding.step2.genderNeutral")}
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>

              <div className="space-y-2">
                <Label>Avatar colour</Label>
                <ChildColorPicker
                  value={avatarColor}
                  onChange={(hex) => { setAvatarColor(hex); setColorManuallySet(true); }}
                />
              </div>

              {agePreview && (
                <p className="text-center text-sm font-medium text-primary animate-page-enter">
                  {agePreview} 🔥
                </p>
              )}

              {/* Optional avatar */}
              <div className="flex flex-col items-center gap-2 pt-2">
                <div className="relative inline-block">
                  {avatarPreviewUrl ? (
                    <button
                      type="button"
                      onClick={() => avatarInputRef.current?.click()}
                      className="rounded-full overflow-hidden"
                      style={{
                        width: 64,
                        height: 64,
                        padding: 0,
                        border: "none",
                        boxShadow: `0 0 0 2px ${avatarColor}`,
                      }}
                    >
                      <img
                        src={avatarPreviewUrl}
                        alt="Preview"
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    </button>
                  ) : (
                    <ChildAvatar
                      child={{ name: childName || "?", avatar_url: null, avatar_color: avatarColor }}
                      size={64}
                      showEditRing
                      onClick={() => avatarInputRef.current?.click()}
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => avatarInputRef.current?.click()}
                    aria-label={t("aria.addPhoto")}
                    className="absolute bottom-0 right-0 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md border-2 border-background transition-transform active:scale-95"
                  >
                    <Camera className="h-3 w-3" />
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("onboarding.step2.photoHint")}
                </p>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="sr-only"
                  onChange={handleAvatarFileChange}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="h-12 flex-1"
                onClick={() => setStep(1)}
              >
                {t("common.back")}
              </Button>
              <Button
                className="h-12 flex-1 text-base font-medium"
                onClick={handleStep2}
                disabled={!childName.trim() || !birthdate}
              >
                {t("common.continue")}
              </Button>
            </div>

            <button
              type="button"
              onClick={handleSkipChild}
              className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
            >
              {t("onboarding.step2.skipForNow")}
            </button>

            {avatarImageSrc && (
              <AvatarCropper
                imageSrc={avatarImageSrc}
                onComplete={handleAvatarCropDone}
                onCancel={() => setAvatarImageSrc(null)}
              />
            )}
          </div>
        )}

        {/* ── Step 3: Language ─────────────────────────────────────────────── */}
        {step === 3 && (
          <div className="space-y-6 animate-page-enter">
            <div className="text-center">
              <h1 className="font-display text-3xl tracking-tight text-foreground">
                {t("onboarding.step3.heading")}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {t("onboarding.step3.subtext")}
              </p>
            </div>

            <div className="space-y-3">
              {langsLoading ? (
                <div className="space-y-3">
                  {[1, 2].map((i) => (
                    <div key={i} className="h-16 rounded-2xl bg-muted animate-pulse" />
                  ))}
                </div>
              ) : (
                languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`w-full flex items-center gap-4 rounded-2xl bg-card px-5 py-4 border-2 text-left transition-colors ${
                      language === lang.code
                        ? "border-primary"
                        : "border-border/50 hover:border-border"
                    }`}
                  >
                    <span className="text-3xl">{lang.flag_emoji}</span>
                    <span className={`text-base font-medium ${language === lang.code ? "text-primary" : "text-foreground"}`}>
                      {lang.name}
                    </span>
                  </button>
                ))
              )}
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="h-12 flex-1"
                onClick={() => setStep(2)}
              >
                {t("common.back")}
              </Button>
              <Button
                className="h-12 flex-1 text-base font-medium"
                onClick={() => setStep(4)}
              >
                {t("common.continue")}
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 4: Mori's language ──────────────────────────────────────── */}
        {step === 4 && (
          <div className="space-y-6 animate-page-enter">
            <div className="text-center">
              <h1 className="font-display text-3xl tracking-tight text-foreground">
                {t("onboarding.step4.heading")}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {t("onboarding.step4.subtext")}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="contentLanguage">
                {t("onboarding.step4.label")}
              </Label>
              <Select
                value={contentLanguage}
                onValueChange={(val) => {
                  setContentLanguage(val);
                  setContentLanguageTouched(true);
                }}
              >
                <SelectTrigger
                  id="contentLanguage"
                  className="h-12 text-base bg-card"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {contentLanguages.map((lang) => (
                    <SelectItem key={lang.code} value={lang.code}>
                      {lang.name_native}
                      {lang.name_native !== lang.name_en
                        ? ` — ${lang.name_en}`
                        : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t("onboarding.step4.help")}
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="h-12 flex-1"
                onClick={() => setStep(3)}
                disabled={saving}
              >
                {t("common.back")}
              </Button>
              <Button
                className="h-12 flex-1 text-base font-medium"
                onClick={handleComplete}
                disabled={saving}
              >
                {saving ? t("onboarding.step3.lightingButton") : t("onboarding.step3.lightFireButton")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingFlow;
